"use client";
import React, { useEffect, useState } from "react";
import { ConfigProvider, ConfigProviderProps, Image, message } from "antd";
import "antd/dist/reset.css";
import { AntdRegistry } from "@ant-design/nextjs-registry";
import { px2remTransformer, StyleProvider } from "@ant-design/cssinjs";
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import dayjs from "dayjs";
import useLangStore from "@/store/useLangStore";
import { langs } from "@/static/locales";

type Locale = ConfigProviderProps["locale"];

const px2rem = px2remTransformer({
  mediaQuery: true, // 媒体查询是否生效
  rootValue: 10, // 32px = 1rem; @default 16
});

const AntdConfigProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  // 利用结构方式获取初始值和内部函数
  const { lang, setLang } = useLangStore((state) => state);
  const [locale, setLocal] = useState<Locale>();

  message.config({
    maxCount: 1,
  });

  useEffect(() => {
    dayjs.locale("en");
    if (lang === langs[0]) {
      setLocal(enUS);
      dayjs.locale("en");
    } else {
      setLocal(zhCN);
      dayjs.locale("zh-cn");
    }
  }, [lang]);

  return (
    <ConfigProvider
      locale={locale}
      spin={{
        indicator: (
          <Image
            preview={false}
            src={"/img/index/Loading.gif"}
            alt="Loading"
            width={48}
            height={48}
            style={{width: "48px", height: "48px"}}
          />
        ),
      }}
      theme={{
        token: {
          colorPrimary: "#402C22",
          colorInfo: "inherit",
          colorBgBase: "#F3F2EC",
        },
        components: {
          // Message配置不起作用
          Message: {
            colorText: "#ff4d4f", // 文本颜色
            contentBg: "#f6ffed", // 背景颜色
            borderRadius: 48, // 圆角
            fontSize: 16, // 字体大小
            contentPadding: 55,
          },
          Collapse: {
            colorTextHeading: "#fff",
            colorBorder: "#444",
            headerPadding: "12px 0",
            contentPadding: "16px 0",
            motionDurationMid: "0.3s",
          },
          Button: {
            controlHeight: 48,
            paddingInline: 10,
            borderRadius: 2,
            controlHeightSM: 42,
            paddingInlineSM: 10,
            borderRadiusSM: 2,
            controlHeightLG: 52,
            paddingInlineLG: 10,
            borderRadiusLG: 2,
            colorBorder: "#C6C0BD",
            paddingBlock: 12,
            lineWidth: 1.5,
            colorBgContainerDisabled: "#C6C0BD",
            borderColorDisabled: "transparent",
            colorTextDisabled: "#fff",
            defaultBg: "#F3F2EC",
            defaultColor: "#402C22",
            defaultActiveBorderColor: "#C6C0BD",
            defaultHoverBorderColor: "#C6C0BD",
            primaryShadow: "transparent",
          },
          Drawer: {
            padding: 12,
            paddingLG: 16,
            colorSplit: "#E5E6EB",
          },
          Divider: {
            colorSplit: "#DEDBD5",
            marginLG: 24,
          },
          Checkbox: {
            colorPrimary: "#402C22",
            colorPrimaryHover: "#402C22",
            colorPrimaryBorder: "#402C22",
            colorBgContainerDisabled: "#C6C0BD",
            controlInteractiveSize: 20,
            borderRadiusSM: 0,
            colorBorder: "#C6C0BD",
          },
          List: {
            colorBorder: "#E5E6EB",
          },
          Form: {
            labelRequiredMarkColor:'#C31010',
            colorError: '#C31010', // 这里可以修改默认错误提示颜色,
          },
          InputNumber: {
            borderRadius: 2,
            borderRadiusLG: 2,
          },
          Input: {
            controlHeight: 48,
            paddingInline: 10,
            borderRadius: 2,
            controlHeightSM: 42,
            paddingInlineSM: 10,
            borderRadiusSM: 2,
            controlHeightLG: 52,
            paddingInlineLG: 10,
            borderRadiusLG: 2,
            colorBorder: "#402C22",
            colorTextPlaceholder: "#796B64",
            colorText: "#402C22",
            controlOutlineWidth: 0,
            lineWidth: 1.5,
            colorError: '#C31010', // 这里可以修改默认错误提示颜色,
          },
          Breadcrumb: {
            itemColor: "#796B64",
            linkColor: "#796B64",
            lastItemColor: "#402C22",
            separatorColor: "#DEDBD5",
          },
          Select: {
            colorBorder: "#C6C0BD",
            colorText: "#402C22",
            colorTextPlaceholder: "#796B64",
            controlOutlineWidth: 0,
            lineWidth: 1.5,
            borderRadiusLG: 2,
            borderRadius: 2,
            colorError: '#C31010', // 这里可以修改默认错误提示颜色,
          },
          Radio: {
            radioSize: 20,
            dotSize: 8,
            colorBorder: "#C6C0BD",
          },
          Descriptions: {
            labelBg: "#C6C0BD",
            borderRadiusLG: 2,
            borderRadius: 2,
          },
          Steps: {
            colorSplit: "#C6C0BD",
          },
          Tabs: {
            itemColor: "#796B64",
            lineHeight: 1.5,
            inkBarColor: "#402C22",
            colorBorderSecondary: "#DEDBD5",
            colorPrimaryBorder: "#DEDBD5",
          },
          Popover: {
            borderRadiusXS: 2,
            borderRadiusLG: 2,
          },
          Card: {
            colorBorderSecondary: "#C6C0BD",
            borderRadius: 2,
            borderRadiusLG: 2,
            lineWidth: 1.5,
          },
          Upload: {
            colorBorder: "#C6C0BD",
            borderRadius: 0,
            borderRadiusLG: 0,
          },
          Modal: {
            borderRadius: 2,
            borderRadiusLG: 2,
            borderRadiusSM: 2,
          },
          Skeleton: {
            borderRadius: 0
          }
        },
      }}
    >
      {/* 此注释的原因在于，transformers={[px2rem]} 会使tailwind 响应式，覆盖antd 的响应式 */}
      {/* transformers={[px2rem]} */}
      <StyleProvider>{children}</StyleProvider>
    </ConfigProvider>
  );
};

const AntdStyleRegistry: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <AntdRegistry>
      <AntdConfigProvider>{children}</AntdConfigProvider>
    </AntdRegistry>
  );
};

export default AntdStyleRegistry;
